import { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import chargerActions from '../store/actions/chargerActions';


const AfterLoginHandler = ({ chargerReducer, userInfoReducer, chargerActions, authReady = true }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const hasNavigatedRef = useRef(false); // Track if initial navigation has been completed
    const hasRunInitialRedirectRef = useRef(false); // Run redirect logic only ONCE when chargers first load
    const hasUserNavigatedRef = useRef(false); // Track if user navigated away from "/"

    // Track user navigation away from root using useLocation (replaces history.listen)
    useEffect(() => {
        if (location.pathname !== '/') {
            hasUserNavigatedRef.current = true;
        }
    }, [location.pathname]);

    const findChargerWithOnGoingTransaction = useCallback((userId, chargersArray) => {
        for (let charger of chargersArray) {
            if (charger.status === 'Connected') {
                for (let connector of charger.connectors) {
                    if ((connector.status === "Charging" || connector.status === "SuspendedEV" || connector.status === "SuspendedEVSE" || connector.status === "Finishing") && connector.transaction?.initiator?.initiatorUserId?.toLowerCase() === userId) {
                        // Create a deep copy of the charger and modify its connectors list to include only the found connector.
                        const foundCharger = { ...charger, connectors: [connector] };
                        return foundCharger;
                    }
                }
            }
        }
        return null;
    }, []);

    // Helper function to detect old format QR code URLs
    const isOldFormatQRCode = useCallback((path) => {
        // Pattern: /ChargePointStation/{id}/connector/{connectorId}
        // Supports both UUIDs and numeric IDs
        const oldFormatPattern = /^\/ChargePointStation\/[^/]+\/connector\/[^/]+$/;
        return oldFormatPattern.test(path);
    }, []);

    // User is already on a valid app route (not "/" or auth) - don't override with connector/default
    const isOnValidAppRoute = useCallback((path) => {
        if (!path || path === '/') return false;
        const validPrefixes = ['/business-group', '/ChargePointStation', '/favChargers', '/myGroups', '/addGroup', '/editGroup', '/Driver', '/strategy', '/locations', '/groups', '/myCars', '/addCar', '/addCharger', '/settings', '/personal', '/payments', '/bills', '/updateBilling', '/RFIDCards', '/OperatorChargePoints', '/QR', '/accessibility-statement', '/privacyPolicy'];
        return validPrefixes.some(prefix => path === prefix || path.startsWith(prefix + '/'));
    }, []);

    const handleNavigation = useCallback((userId, chargers) => {
        const currentPath = location.pathname; // Extract the current path

        // If user has already navigated to a valid app route (e.g. business-group, edit), don't override
        if (isOnValidAppRoute(currentPath)) {
            hasNavigatedRef.current = true;
            return;
        }

        // Check if user is already on a valid connector or charge point route
        // Updated to support UUIDs (not just numeric IDs)
        const isOnConnectorRoute = /^\/ChargePointStation\/[^/]+\/connector\/[^/]+$/.test(currentPath);
        const isOnChargePointRoute = /^\/ChargePointStation\/[^/]+$/.test(currentPath);
        const isOnChargePointListRoute = currentPath === '/ChargePointStation' || currentPath === '/favChargers';
        
        // If already navigated and user is on a valid route, don't interfere
        if (hasNavigatedRef.current && (isOnConnectorRoute || isOnChargePointRoute || isOnChargePointListRoute)) {
            return;
        }

        const redirectTo = (path) => {
            // Always redirect to the provided path, not currentPath
            // This ensures we navigate to the saved post-login redirect URL
            navigate(path, { replace: true });
            hasNavigatedRef.current = true; // Mark navigation as complete
        };

        // Check if we're already on a QR code route (new format: /:partyID/:uid/:connector_id)
        const newFormatQRPattern = /^\/([A-Za-z0-9]+)\/(\d+)\/(\d+)$/;
        const isOnNewFormatQRRoute = newFormatQRPattern.test(currentPath);
        
        // Check if we're already on old format QR code route
        const isOnOldFormatQRRoute = isOldFormatQRCode(currentPath);

        const postLoginRedirected = sessionStorage.getItem('postLoginRedirected');
        
        // If we're already on a QR code route (new or old), don't interfere - let route handlers process it
        if (isOnNewFormatQRRoute || isOnOldFormatQRRoute) {
            hasNavigatedRef.current = true;
            return;
        }
        
        if (!postLoginRedirected) {
            const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
            
            if (postLoginRedirect) {
                // Check if the saved redirect is a new format QR code route
                const isNewFormatQRRedirect = newFormatQRPattern.test(postLoginRedirect);
                
                // Check if the saved redirect is an old format QR code route
                const isOldFormatQRRedirect = isOldFormatQRCode(postLoginRedirect);
                
                if (isNewFormatQRRedirect) {
                    // New format QR code - don't remove sessionStorage yet - let QRCodeRouteHandler clear it after successful conversion
                    // Only set the flag to prevent multiple redirects
                    sessionStorage.setItem('postLoginRedirected', 'processing');
                    redirectTo(postLoginRedirect);
                } else if (isOldFormatQRRedirect) {
                    // Old format QR code - don't remove sessionStorage yet - let OldFormatQRCodeRouteHandler clear it after successful validation
                    // Only set the flag to prevent multiple redirects
                    sessionStorage.setItem('postLoginRedirected', 'processing');
                    redirectTo(postLoginRedirect);
                } else {
                    // Other route - safe to remove immediately
                    redirectTo(postLoginRedirect);
                    sessionStorage.removeItem('postLoginRedirect');
                    sessionStorage.setItem('postLoginRedirected', true);
                }
            }
            else {
                // Only redirect to connector/favChargers/ChargePointStation when user is still on root "/"
                // Never override user navigation (e.g. /business-group, /business-group/edit/xxx)
                if (currentPath !== '/' || hasUserNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    return;
                }
                const foundCharger = findChargerWithOnGoingTransaction(userId, chargers);
                if (foundCharger) {
                    const chargerId = foundCharger.id;
                    const connectorId = foundCharger.connectors[0].id;
                    redirectTo(`/ChargePointStation/${chargerId}/connector/${connectorId}`);
                } else if (chargerReducer.favoriteChargersIds.length > 0) {
                    redirectTo(`/favChargers`);
                } else {
                    redirectTo(`/ChargePointStation`);
                }
            }
        } else {
            // Check if redirect was completed (QRCodeRouteHandler or OldFormatQRCodeRouteHandler sets this to 'completed')
            const redirectStatus = sessionStorage.getItem('postLoginRedirected');
            if (redirectStatus === 'completed') {
                sessionStorage.removeItem('postLoginRedirected');
                hasNavigatedRef.current = true;
            } else if (redirectStatus === 'processing') {
                // Don't interfere - QRCodeRouteHandler or OldFormatQRCodeRouteHandler is processing
            } else if (redirectStatus === 'true') {
                // Other redirect completed - mark as navigated
                sessionStorage.removeItem('postLoginRedirected');
                hasNavigatedRef.current = true;
            }
        }
    }, [location.pathname, navigate, chargerReducer.favoriteChargersIds, findChargerWithOnGoingTransaction, isOldFormatQRCode, isOnValidAppRoute]);

    useEffect(() => {
        // Gate on authReady - do NOT run redirect logic until auth bootstrap is complete
        // This prevents interfering with routing during the auth initialization phase
        if (!authReady) {
            return;
        }
        
        // Run redirect logic only ONCE when chargers first load - never re-run on later path/reducer changes
        if (chargerReducer.stateInfo.isInitialFetch && !hasRunInitialRedirectRef.current) {
            hasRunInitialRedirectRef.current = true;
            const { chargersArray, driverChargersArray, publicChargersArray } = chargerReducer.chargers;
            const chargers = [...new Set([...chargersArray, ...driverChargersArray, ...publicChargersArray])];
            handleNavigation(userInfoReducer.userInfo.userId, chargers);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargerReducer.stateInfo.isInitialFetch, userInfoReducer.userInfo.userId, handleNavigation, authReady, location.pathname]);

    return null;
};

const mapStateToProps = (state) => ({
    userInfoReducer: state.userInfoReducer,
    chargerReducer: state.chargerReducer,
});

const mapDispatchToProps = (dispatch) => {
    return {
        chargerActions: bindActionCreators(chargerActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AfterLoginHandler);